import axios, { AxiosInstance } from 'axios';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';

export interface DemographicUpdate {
  ref_demographic_id: number;
  ref_user_id: number;
  demographic_value: string;
}

export interface AccountData {
  firstName: string;
  lastName: string;
  middleName: string;
  dateOfBirth: string;
  phone: string;
}

// Get API base URL from environment variable (like diagnostics)
// Community API Configuration
// Production: community-api.lore.co
// Non-production: community-api.lorehealthcare.io
const API_BASE_URL =
  process.env.REACT_APP_COMMUNITY_API_BASE_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://community-api.lore.co'
    : 'https://community-api.lorehealthcare.io');

console.log('[MenuMFE] Account Service API Base URL:', API_BASE_URL);

// Create axios instance with default config (like diagnostics)
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add response interceptor for error handling (like diagnostics)
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      console.error('[MenuMFE] Authentication failed');
    } else if (error.response?.status === 403) {
      console.error('[MenuMFE] Authorization failed: Access denied');
      const serverMessage =
        error.response?.data?.message || error.response?.data || 'Access denied';
      throw new Error(serverMessage);
    }
    return Promise.reject(error);
  }
);

// Account API methods (following diagnostics pattern)
export const accountApi = {
  fetchAccountData: async (refUserId: number): Promise<AccountData> => {
    try {
      console.log('[MenuMFE] Fetching account data for user:', refUserId);
      const response = await api.get(`/api/userdemographic/user/${refUserId}`, {
        headers: {
          'X-User-ID': refUserId.toString(),
          Authorization: `Bearer ${CommunicationBridge.getAccessToken()}`,
        },
      });

      console.log('[MenuMFE] Account data response:', response.data);

      // Map the demographics response to AccountData format
      const demographics = response.data;
      return {
        firstName:
          demographics.find((d: DemographicUpdate) => d.ref_demographic_id === 1)
            ?.demographic_value || '',
        lastName:
          demographics.find((d: DemographicUpdate) => d.ref_demographic_id === 2)
            ?.demographic_value || '',
        middleName:
          demographics.find((d: DemographicUpdate) => d.ref_demographic_id === 3)
            ?.demographic_value || '',
        dateOfBirth:
          demographics.find((d: DemographicUpdate) => d.ref_demographic_id === 7)
            ?.demographic_value || '',
        phone:
          demographics.find((d: DemographicUpdate) => d.ref_demographic_id === 10)
            ?.demographic_value || '',
      };
    } catch (error) {
      console.error('[MenuMFE] Error fetching account data:', error);
      throw error;
    }
  },

  updateAccount: async (data: DemographicUpdate[], refUserId: number): Promise<void> => {
    try {
      console.log('[MenuMFE] Updating account for user:', refUserId, 'with data:', data);
      await api.put(`/api/userdemographic/user/${refUserId}`, data, {
        headers: {
          'X-User-ID': refUserId.toString(),
          Authorization: `Bearer ${CommunicationBridge.getAccessToken()}`,
        },
      });
      console.log('[MenuMFE] Account updated successfully');
    } catch (error) {
      console.error('[MenuMFE] Error updating account:', error);
      throw error;
    }
  },
};

// Export singleton for backward compatibility
const accountService = {
  fetchAccountData: accountApi.fetchAccountData,
  updateAccount: accountApi.updateAccount,
};

export default accountService;
